import { skipToken } from '@reduxjs/toolkit/query';
import { useGetSavedBookingPriceQuery } from '../api/booking/pricePreviewApiSlice';
import { PricePreviewStatus } from '../components/reservationSummary/PricePreviewStateRow';

/**
 * The price of a **saved** booking, from the one backend computation (#1203).
 *
 * The create and edit screens describe a form and use `usePricePreview`. A
 * detail view has no form — it has a booking — so it asks with the booking id
 * and the backend resolves object, window, services, adjustment and amount paid
 * from the booking itself. The answer is what the booking is billed at: its
 * booked stay price and its snapshot service prices, never a fresh quote at
 * today's rates.
 *
 * Same return shape as `usePricePreview`, so the summary components render both
 * through one path.
 */
export function useSavedBookingPrice(orderId: string | undefined) {
    const { data: breakdown, isError, isFetching } = useGetSavedBookingPriceQuery(
        orderId ? { orderId } : skipToken,
        // Money, read at the moment an operator wants to know what a change
        // cost: always ask again rather than render a figure from before it.
        { refetchOnMountOrArgChange: true },
    );
    const errorSettled = isError && !isFetching;
    const previewStatus: PricePreviewStatus = errorSettled
        ? 'error'
        : orderId
            ? 'loading'
            : 'idle';
    return { breakdown, previewStatus };
}
